import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';
import { ArrowLeft, FileText, Download, DollarSign, Calendar, MapPin, Users, Eye, Layers, ExternalLink } from 'lucide-react';
import { formatTimeRange, formatDateMMDDYYYY, formatDateMonthDay } from '../../utils/timeFormat';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Iter-312 — Stable subcomponents so nested conditional JSX rendered
// inside a `.map()` iterator survives the visual-editor babel plugin.
// (Deep AND/OR chains inside map bodies were being stripped from the
// output HTML — extracting them into named function components is the
// safest workaround.)
const EventLinkHeader = ({ inv, onNavigate }) => {
  const label = inv.event_title || inv.venue_name;
  if (!label) return null;
  if (inv.shift_id) {
    return (
      <div className="mb-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(`/client/shifts/${inv.primary_shift_id || inv.shift_id}`);
          }}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-900 hover:underline"
          data-testid={`invoice-event-link-${inv.id}`}
          title="View event details"
        >
          {label}
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="mb-2">
      <span
        className="text-sm font-semibold text-gray-900"
        data-testid={`invoice-event-title-${inv.id}`}
      >
        {label}
      </span>
    </div>
  );
};

const EventDateSpan = ({ inv }) => {
  const start = inv.event_date_start || inv.gig_date;
  if (!start) return null;
  const end = inv.event_date_end || inv.gig_date;
  const label = end && start !== end
    ? `${formatDateMonthDay(start)} → ${formatDateMonthDay(end)}`
    : formatDateMonthDay(start);
  return (
    <span className="flex items-center gap-1" data-testid={`invoice-event-date-${inv.id}`}>
      <Calendar className="w-3.5 h-3.5 shrink-0" />
      {label}
    </span>
  );
};

const ClientInvoices = () => {
  const navigate = useNavigate();
  const { invoiceId: urlInvoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Iter-313d — "Preview as Client" support. Admin sessions land here with
  // ?as_client=<uuid> so the backend scopes to that specific client's
  // owner tree (not the admin's own empty tree).
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const isAdminSession = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const asClient = searchParams.get('as_client');
  const isAdminPreview = isAdminSession && !!asClient;
  const asClientQs = asClient ? `?as_client=${encodeURIComponent(asClient)}` : '';

  const viewInvoiceDetail = useCallback(async (invoiceId) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/api/invoices/client/${invoiceId}${asClientQs}`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err?.detail?.message || err?.detail || `Request failed (${res.status})`;
        throw new Error(msg);
      }
      const data = await res.json();
      setSelectedInvoice(data);
    } catch (e) {
      toast.error(e.message || 'Failed to load invoice details');
    } finally {
      setDetailLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asClientQs]);

  const [ownerOnly, setOwnerOnly] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/invoices/client${asClientQs}`, { headers });
      if (res.status === 403) {
        const body = await res.json().catch(() => ({}));
        if (body?.detail?.error === 'owner_only') {
          setOwnerOnly(true);
          setInvoices([]);
          return;
        }
        toast.error(body?.detail?.message || body?.detail || 'Access denied');
        setInvoices([]);
        return;
      }
      if (res.status === 400 && isAdminSession && !asClient) {
        // Admin browsed straight to /client/invoices without a target client.
        setInvoices([]);
        return;
      }
      if (!res.ok) {
        setInvoices([]);
        return;
      }
      const data = await res.json();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asClientQs, isAdminSession, asClient]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Auto-open the detail dialog when /client/invoices/:id is hit
  useEffect(() => {
    if (urlInvoiceId) {
      viewInvoiceDetail(urlInvoiceId);
    }
  }, [urlInvoiceId, viewInvoiceDetail]);

  const handleCloseDialog = () => {
    setSelectedInvoice(null);
    if (urlInvoiceId) {
      // Drop the id from the URL so the user can navigate cleanly
      navigate('/client/invoices', { replace: true });
    }
  };

  /** Download an invoice PDF. Uses fetch+blob to avoid XHR→window-navigation (which
   *  could bounce through the 401 interceptor and confuse admins).  */
  const downloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await fetch(`${API}/api/invoices/${invoiceId}/pdf`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (e) {
      toast.error(e.message || 'Download failed');
    }
  };

  const printPdf = async (invoiceId) => {
    try {
      const res = await fetch(`${API}/api/invoices/${invoiceId}/pdf`, { headers });
      if (!res.ok) throw new Error(`Preview failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) {
        w.addEventListener('load', () => {
          try { w.focus(); w.print(); } catch { /* popup print blocked */ }
        });
      }
    } catch (e) {
      toast.error(e.message || 'Print preview failed');
    }
  };

  const getStatusBadge = (inv) => {
    if (inv.is_paid) return <Badge className="bg-green-100 text-green-800" data-testid="invoice-status-paid">Paid</Badge>;
    return <Badge className="bg-amber-100 text-amber-800" data-testid="invoice-status-pending">{inv.status || 'Issued'}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden" data-testid="invoices-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/client/dashboard')}
            className="text-gray-600 hover:text-gray-900"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-lg font-semibold text-gray-900">Invoices</h1>
          <div className="w-24" />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isAdminPreview && (
          <div
            className="mb-4 rounded-md border border-purple-200 bg-purple-50 px-4 py-2 flex items-center gap-2 text-purple-800 text-sm"
            data-testid="admin-preview-banner"
          >
            <Eye className="w-4 h-4" />
            <span>
              <strong>Admin preview.</strong> Viewing invoices as the client owner — no data is being changed on their behalf.
            </span>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
          </div>
        ) : ownerOnly ? (
          <Card className="bg-amber-50 border-amber-200" data-testid="invoices-owner-only">
            <CardContent className="py-10 px-4 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                <FileText className="w-7 h-7 text-amber-600" />
              </div>
              <p className="text-base font-semibold text-gray-900 mb-1">Invoices are owner-only</p>
              <p className="text-sm text-gray-600 max-w-md mx-auto mb-5">
                Only the company owner can view or manage invoices. Ask your owner to open this page,
                or manage teammates from Settings &gt; Team.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/client/settings/team')}
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
                data-testid="invoices-open-team-cta"
              >
                Open Settings &gt; Team
              </Button>
            </CardContent>
          </Card>
        ) : invoices.length === 0 ? (
          <Card className="bg-white border-gray-200" data-testid="invoices-empty">
            <CardContent className="py-12 px-4 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-cyan-50 flex items-center justify-center">
                <FileText className="w-7 h-7 text-cyan-500" />
              </div>
              <p className="text-base font-semibold text-gray-900 mb-1">No invoices yet</p>
              <p className="text-sm text-gray-500 max-w-md mx-auto mb-5">
                Invoices are generated automatically when a booking is confirmed — you&apos;ll see deposit or full-payment invoices here before your event.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <Button
                  size="sm"
                  onClick={() => navigate('/client/discover')}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  data-testid="invoices-discover-cta"
                >
                  Browse Talent
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/client/billing')}
                  className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  data-testid="invoices-billing-cta"
                >
                  View Billing Overview
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {invoices.map((inv) => (
              <Card
                key={inv.id}
                className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                data-testid="invoice-card"
                onClick={() => viewInvoiceDetail(inv.id)}
              >
                <CardContent className="p-5" onClick={(e) => {
                  // Action button area handles its own clicks; this catches the card body.
                  if (e.target.closest('button')) return;
                }}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 break-words" data-testid="invoice-number">{inv.invoice_number}</h3>
                        {getStatusBadge(inv)}
                        {/* Iter-224f — Consolidation pill: one invoice covering multiple event dates */}
                        {inv.consolidated_pill && (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 font-medium"
                            data-testid={`client-invoice-consolidated-pill-${inv.id}`}
                            title="This invoice covers multiple consecutive event dates"
                          >
                            <Layers className="w-3 h-3" />
                            {inv.consolidated_pill.label}
                          </span>
                        )}
                      </div>
                      {/* Iter-312 — Prominent event title with clickable link to event */}
                      <EventLinkHeader inv={inv} onNavigate={navigate} />
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1 min-w-0">
                          <MapPin className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">
                            {[inv.venue_name, inv.city].filter(Boolean).join(' · ') || '—'}
                          </span>
                        </span>
                        <EventDateSpan inv={inv} />
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5 shrink-0" />
                          {inv.talent_count} talent
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 sm:text-right shrink-0">
                      <div>
                        <p className="text-xl font-bold text-gray-900" data-testid="invoice-amount">${inv.total_amount.toFixed(2)}</p>
                        {inv.issue_date && (
                          <p className="text-xs text-gray-400">Issued {formatDateMonthDay(inv.issue_date)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewInvoiceDetail(inv.id)}
                          className="border-gray-300"
                          data-testid="view-invoice-btn"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadPdf(inv.id, inv.invoice_number)}
                          className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                          data-testid="download-invoice-pdf-btn"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      <Dialog open={!!selectedInvoice} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent
          className="max-w-2xl w-[calc(100vw-1rem)] bg-white max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          data-testid="invoice-detail-modal"
        >
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <DialogTitle className="text-gray-900">Invoice {selectedInvoice?.invoice_number}</DialogTitle>
          <DialogDescription className="sr-only">Invoice</DialogDescription>
              {selectedInvoice && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPdf(selectedInvoice.id, selectedInvoice.invoice_number)}
                    className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    data-testid="modal-download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printPdf(selectedInvoice.id)}
                    className="border-gray-300"
                    data-testid="modal-print-btn"
                  >
                    Print
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Iter-312 — Prominent event header (title + date range + location + link) */}
              {(selectedInvoice.event_title || selectedInvoice.venue_name) && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4" data-testid="invoice-event-header">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider text-cyan-700 font-semibold mb-1">Event</p>
                      <p className="text-lg font-bold text-gray-900" data-testid="invoice-event-title">
                        {selectedInvoice.event_title || selectedInvoice.venue_name}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-700">
                        {(selectedInvoice.event_date_start || selectedInvoice.gig_date) && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {selectedInvoice.event_date_start && selectedInvoice.event_date_end && selectedInvoice.event_date_start !== selectedInvoice.event_date_end
                              ? `${formatDateMMDDYYYY(selectedInvoice.event_date_start)} → ${formatDateMMDDYYYY(selectedInvoice.event_date_end)}`
                              : formatDateMMDDYYYY(selectedInvoice.event_date_start || selectedInvoice.gig_date)}
                          </span>
                        )}
                        {(selectedInvoice.venue_name || selectedInvoice.city) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {[selectedInvoice.venue_name, selectedInvoice.city, selectedInvoice.state].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedInvoice.shift_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Iter-314 — Classic View invoice fix.
                          // Preserve the `?as_client=<uuid>` impersonation
                          // context so admins previewing as a client
                          // don't lose their scope on navigation and
                          // land on the empty "No shifts yet" list.
                          const dest = `/client/shifts/${selectedInvoice.primary_shift_id || selectedInvoice.shift_id}`;
                          const params = new URLSearchParams(window.location.search);
                          const asClient = params.get('as_client');
                          navigate(asClient ? `${dest}?as_client=${asClient}` : dest);
                        }}
                        className="border-cyan-300 text-cyan-700 hover:bg-cyan-100 shrink-0"
                        data-testid="invoice-detail-event-link-btn"
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> View event
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {/* Header info */}
              <div className="flex justify-between items-start">
                <div>
                  {selectedInvoice.client_logo_url && (
                    <img src={selectedInvoice.client_logo_url} alt="Logo" className="h-10 mb-2 object-contain" />
                  )}
                  <p className="font-medium text-gray-900">{selectedInvoice.client_company_name}</p>
                  <p className="text-sm text-gray-500">{selectedInvoice.client_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Issue Date</p>
                  <p className="font-medium text-gray-900">
                    {selectedInvoice.issue_date ? formatDateMonthDay(selectedInvoice.issue_date) : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">Due Date</p>
                  <p className="font-medium text-gray-900">
                    {selectedInvoice.due_date ? formatDateMMDDYYYY(selectedInvoice.due_date) : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Gig Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Gig Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="flex gap-2 min-w-0">
                    <span className="text-gray-500 shrink-0">Role:</span>
                    <span className="text-gray-900 truncate">{selectedInvoice.role_name}</span>
                  </div>
                  <div className="flex gap-2 min-w-0">
                    <span className="text-gray-500 shrink-0">Venue:</span>
                    <span className="text-gray-900 truncate">{selectedInvoice.venue_name}</span>
                  </div>
                  <div className="flex gap-2 min-w-0">
                    <span className="text-gray-500 shrink-0">Date:</span>
                    <span className="text-gray-900">
                      {selectedInvoice.gig_date ? formatDateMonthDay(selectedInvoice.gig_date) : 'N/A'}
                    </span>
                  </div>
                  <div className="flex gap-2 min-w-0">
                    <span className="text-gray-500 shrink-0">Time:</span>
                    <span className="text-gray-900 truncate">
                      {formatTimeRange(selectedInvoice.gig_start_time, selectedInvoice.gig_end_time)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Line Items — Iter-213+ — surface talent name, role,
                  actual vs billable hours, min applied, OT premium so
                  clients can fully understand each row.
                  Iter-223b — show Event/Date/Venue context line above the table
                  so clients forwarding the PDF to accounting see the full picture. */}
              {selectedInvoice.line_items?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-3">Line Items</h4>
                  {(() => {
                    const first = selectedInvoice.line_items[0] || {};
                    const ev = first.event_name || selectedInvoice.event_name || '';
                    const dt = first.date_display || first.date || selectedInvoice.shift_date || '';
                    const vn = first.venue_name || selectedInvoice.venue_name || '';
                    if (!ev && !dt && !vn) return null;
                    return (
                      <div className="mb-2 px-3 py-2 bg-cyan-50/60 border border-cyan-100 rounded text-xs text-gray-700" data-testid="invoice-event-context">
                        {ev && <span className="font-semibold text-gray-900">{ev}</span>}
                        {dt && <span className="ml-2">· {dt}</span>}
                        {vn && <span className="ml-2">· {vn}</span>}
                      </div>
                    );
                  })()}
                  <div className="border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]" data-testid="invoice-line-items-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 text-gray-600 font-medium">Talent</th>
                          <th className="text-left p-3 text-gray-600 font-medium">Role</th>
                          <th className="text-right p-3 text-gray-600 font-medium">Actual hrs</th>
                          <th className="text-right p-3 text-gray-600 font-medium">Min hrs</th>
                          <th className="text-right p-3 text-gray-600 font-medium">Billed hrs</th>
                          <th className="text-right p-3 text-gray-600 font-medium">OT hrs</th>
                          <th className="text-right p-3 text-gray-600 font-medium">Rate</th>
                          <th className="text-right p-3 text-gray-600 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedInvoice.line_items.map((item, idx) => {
                          const billed = item.hours ?? item.hours_worked ?? null;
                          const actual = item.actual_hours ?? null;
                          const minHrs = item.min_hours ?? null;
                          const otHrs = item.overtime_hours ?? 0;
                          const isBackup = (item.kind || '').includes('backup');
                          const isActivated = item.kind === 'backup_activated';
                          return (
                          <tr key={idx} data-testid={`invoice-line-item-${idx}`}>
                            <td className="p-3 text-gray-900">
                              <div className="font-medium">{item.talent_name || item.description || '—'}</div>
                              {isBackup && (
                                <span className={`inline-block mt-0.5 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${
                                  isActivated
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-amber-100 text-amber-800'
                                }`}>
                                  {isActivated ? 'Backup activated' : 'Backup standby'}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-gray-700">{item.role_name || '—'}</td>
                            <td className="p-3 text-right text-gray-700">{actual != null ? Number(actual).toFixed(2) : '—'}</td>
                            <td className="p-3 text-right text-gray-700">{minHrs != null ? Number(minHrs).toFixed(2) : '—'}</td>
                            <td className="p-3 text-right text-gray-900 font-medium">{billed != null ? Number(billed).toFixed(2) : '—'}</td>
                            <td className="p-3 text-right text-gray-700">
                              {otHrs > 0 ? (
                                <span className="text-amber-700 font-medium" title={`OT premium $${Number(item.overtime_amount || 0).toFixed(2)}`}>
                                  {Number(otHrs).toFixed(2)}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="p-3 text-right text-gray-700">${(item.rate ?? 0).toFixed(2)}/hr</td>
                            <td className="p-3 text-right font-medium text-gray-900">${(item.amount ?? item.total_price ?? 0).toFixed(2)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Standard primary minimum: 4 hrs · Backup standby minimum: 2 hrs · Overtime: 1.5× over 8 hrs. Min hours may be waived by admin.
                  </p>
                </div>
              )}

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-900">${selectedInvoice.subtotal?.toFixed(2)}</span>
                </div>
                {selectedInvoice.tax_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Tax ({selectedInvoice.tax_rate}%)</span>
                    <span className="text-gray-900">${selectedInvoice.tax_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span className="text-gray-900">Total</span>
                  <span className="text-cyan-600" data-testid="invoice-detail-total">${selectedInvoice.total_amount?.toFixed(2)}</span>
                </div>
                {selectedInvoice.is_paid && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Paid</span>
                    <span>${selectedInvoice.paid_amount?.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {selectedInvoice.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 font-medium mb-1">Notes</p>
                  <p className="text-sm text-gray-700">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientInvoices;
